import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Radio, Modal } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, saveRememberPassword, getRememberPassword } from '../../store/authStore';
import { login, register, loginByCode, sendCode, resetPassword, checkPhoneBound } from '../../api/auth';
import { usernameValidator, passwordValidator, phoneValidator, codeValidator, isPhone } from '../../utils/validate';
import { extractSendCodeMockCode } from '../../utils/sendCodeMock';
import './index.css';
const SEND_CODE_COOLDOWN = 60;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [mode, setMode] = useState<'password' | 'code' | 'register'>('password');
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeCooldown, setCodeCooldown] = useState(0);
  const [forgotCodeCooldown, setForgotCodeCooldown] = useState(0);
  const [forgotCodeLoading, setForgotCodeLoading] = useState(false);
  const [registerCodeCooldown, setRegisterCodeCooldown] = useState(0);
  const [registerCodeLoading, setRegisterCodeLoading] = useState(false);
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [forgotForm] = Form.useForm();
  const navigate = useNavigate();
  const { login: setAuth } = useAuthStore();

  const handleSendCode = async (type: 'login' | 'register' | 'reset' | 'bind', account: string, setCooldown: (n: number) => void, setLoadingState: (b: boolean) => void) => {
    if (!account?.trim()) {
      message.warning('请输入手机号');
      return;
    }
    if (!isPhone(account)) {
      message.warning('请输入正确的手机号');
      return;
    }
    if (type === 'login') {
      const checkRes = await checkPhoneBound(account);
      if (checkRes.code === 200 && checkRes.data && !checkRes.data.bound) {
        message.warning('该手机号未绑定任何账号，请先使用用户名注册，登录后在个人中心绑定手机号');
        return;
      }
    }
    setLoadingState(true);
    try {
      const res = await sendCode({ account, type });
      if (res.code === 200) {
        message.success('验证码已发送');
        const mockCode = extractSendCodeMockCode(res);
        if (mockCode) {
          Modal.info({
            title: '测试验证码（未真实发送短信）',
            content: (
              <div>
                <p className="text-gray-600 mb-2">当前为测试环境，短信未真实发送。</p>
                <p className="text-lg font-mono font-semibold text-blue-600">验证码：{mockCode}</p>
              </div>
            ),
            okText: '知道了',
          });
        }
        let n = SEND_CODE_COOLDOWN;
        setCooldown(n);
        const t = setInterval(() => {
          n -= 1;
          setCooldown(n);
          if (n <= 0) clearInterval(t);
        }, 1000);
      } else {
        message.error(res.message || '发送失败');
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '发送失败');
    } finally {
      setLoadingState(false);
    }
  };

  // 页面加载时检查是否有记住的密码
  useEffect(() => {
    const remembered = getRememberPassword();
    if (remembered) {
      loginForm.setFieldsValue({
        account: remembered.account,
        password: remembered.password,
        rememberPassword: true,
      });
    }
  }, [loginForm]);

  // 登录提交（密码或验证码）
  const onLoginFinish = async (values: any) => {
    setLoading(true);
    try {
      if (mode === 'code') {
        const response = await loginByCode({ account: values.account, code: values.code });
        if (response.code === 200 && response.data) {
          const { token, user } = response.data;
          setAuth(token, user);
          localStorage.setItem('user_info', JSON.stringify(user));
          message.success('登录成功');
          
          // 如果是管理员且当前是新开页面，则根据需要跳转
          const isNewTab = window.opener !== null || window.history.length === 1;
          if (user.role === 'admin' && isNewTab) {
            navigate('/admin');
          } else {
            navigate('/');
          }
        } else {
          message.error(response.message || '登录失败');
        }
        return;
      }
      const response = await login({ account: values.account, password: values.password });
      if (response.code === 200 && response.data) {
        const { token, user } = response.data;
        setAuth(token, user);
        localStorage.setItem('user_info', JSON.stringify(user));
        if (values.rememberPassword) {
          saveRememberPassword(values.account, values.password);
        } else {
          localStorage.removeItem('remember_password');
        }
        message.success('登录成功');
        
        // 如果是管理员且当前是新开页面，则根据需要跳转
        const isNewTab = window.opener !== null || window.history.length === 1;
        if (user.role === 'admin' && isNewTab) {
          navigate('/admin');
        } else {
          navigate('/');
        }
      } else {
        message.error(response.message || '登录失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  // 注册提交（用户名 + 密码）
  const onRegisterFinish = async (values: any) => {
    setRegisterLoading(true);
    try {
      const response = await register({
        username: values.username.trim(),
        password: values.password,
      });
      if (response.code === 200 && response.data) {
        const { token, user } = response.data;
        setAuth(token, user);
        localStorage.setItem('user_info', JSON.stringify(user));
        message.success('注册成功');
        navigate('/');
      } else {
        message.error(response.message || '注册失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || '注册失败，请检查输入信息');
    } finally {
      setRegisterLoading(false);
    }
  };

  const onForgotFinish = async (values: any) => {
    setForgotLoading(true);
    try {
      const res = await resetPassword({
        account: values.account,
        code: values.code,
        newPassword: values.newPassword,
      });
      if (res.code === 200) {
        message.success('密码已重置，请使用新密码登录');
        setForgotModalOpen(false);
        forgotForm.resetFields();
      } else {
        message.error(res.message || '重置失败');
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '重置失败');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-8">
      <Card 
        className="w-full max-w-md shadow-2xl" 
        title={
          <div className="text-center">
            <div className="text-2xl font-semibold text-gray-800">米迦同学-装修AI助手</div>
            <div className="mt-1 text-sm text-gray-500">登录或注册以解锁更多功能</div>
          </div>
        }
        bordered={false}
      >
        <div className="mb-4 w-full flex justify-center">
          <Radio.Group
            value={mode}
            onChange={(e) => {
              const nextMode = e.target.value as 'password' | 'code' | 'register';
              setMode(nextMode);
              loginForm.resetFields();
              registerForm.resetFields();
            }}
            className="login-mode-buttons"
            style={{ width: '100%' }}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: '密码登录', value: 'password' },
              { label: '短信登录', value: 'code' },
              { label: '注册账号', value: 'register' },
            ]}
          />
        </div>

        {mode !== 'register' ? (
          <Form
            form={loginForm}
            name="login"
            onFinish={onLoginFinish}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item
              name="account"
              label={mode === 'code' ? '手机号' : '用户名'}
              rules={mode === 'code' ? [{ validator: phoneValidator }] : [{ validator: usernameValidator }]}
            >
              <Input
                prefix={<UserOutlined className="text-gray-400" />}
                placeholder={mode === 'code' ? '请输入手机号' : '请输入用户名'}
              />
            </Form.Item>

            {mode === 'password' && (
              <Form.Item name="password" label="密码" rules={[{ validator: passwordValidator }]}>
                <Input.Password
                  prefix={<LockOutlined className="text-gray-400" />}
                  placeholder="请输入密码（6-20位字母+数字）"
                />
              </Form.Item>
            )}

            {mode === 'code' && (
              <Form.Item name="code" label="验证码" rules={[{ validator: codeValidator }]}>
                <div className="flex gap-2">
                  <Input
                    prefix={<SafetyCertificateOutlined className="text-gray-400" />}
                    placeholder="请输入6位验证码"
                    maxLength={6}
                  />
                  <Button
                    type="primary"
                    loading={codeLoading}
                    disabled={codeCooldown > 0}
                    onClick={() => {
                      const account = loginForm.getFieldValue('account');
                      handleSendCode('login', account, setCodeCooldown, setCodeLoading);
                    }}
                  >
                    {codeCooldown > 0 ? `${codeCooldown}s 后重发` : '获取验证码'}
                  </Button>
                </div>
              </Form.Item>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                className="h-12 text-base"
              >
                登录
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form
            form={registerForm}
            name="register"
            onFinish={onRegisterFinish}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item name="username" label="用户名" rules={[{ validator: usernameValidator }]}>
              <Input
                prefix={<UserOutlined className="text-gray-400" />}
                placeholder="3-20位字母、数字、下划线或中文"
              />
            </Form.Item>

            <Form.Item name="password" label="密码" rules={[{ validator: passwordValidator }]}>
              <Input.Password
                prefix={<LockOutlined className="text-gray-400" />}
                placeholder="请输入密码（6-20位字母+数字）"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认密码"
              dependencies={['password']}
              rules={[
                { required: true, message: '请再次输入密码' },
                ({ getFieldValue }) => ({
                  validator(_: any, value: string) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-gray-400" />}
                placeholder="请再次输入密码"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={registerLoading} block className="h-12 text-base">
                注册
              </Button>
            </Form.Item>
          </Form>
        )}

        <Modal
          title="找回密码"
          open={forgotModalOpen}
          onCancel={() => {
            setForgotModalOpen(false);
            forgotForm.resetFields();
          }}
          footer={null}
          destroyOnHidden
        >
          <Form
            form={forgotForm}
            name="forgot"
            onFinish={onForgotFinish}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="account"
              label="手机号"
              rules={[{ validator: phoneValidator }]}
            >
              <Input prefix={<UserOutlined />} placeholder="请输入手机号" />
            </Form.Item>
            <Form.Item name="code" label="验证码" rules={[{ validator: codeValidator }]}>
              <div className="flex gap-2">
                <Input placeholder="6位验证码" maxLength={6} />
                <Button
                  type="primary"
                  loading={forgotCodeLoading}
                  disabled={forgotCodeCooldown > 0}
                  onClick={() => {
                    const account = forgotForm.getFieldValue('account');
                    handleSendCode('reset', account, setForgotCodeCooldown, setForgotCodeLoading);
                  }}
                >
                  {forgotCodeCooldown > 0 ? `${forgotCodeCooldown}s 后重发` : '获取验证码'}
                </Button>
              </div>
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[{ validator: passwordValidator }]}
            >
              <Input.Password placeholder="6-20位字母+数字" />
            </Form.Item>
            <Form.Item
              name="confirmNewPassword"
              label="确认新密码"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请再次输入新密码' },
                ({ getFieldValue }) => ({
                  validator(_: any, value: string) {
                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                    return Promise.reject(new Error('两次输入不一致'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="再次输入新密码" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={forgotLoading} block className="h-10">
                确认重置
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default Login;
